import React from 'react';
import { PILL_SHELL, STATUS_TONES } from './pillStyles';
import { EmptyValue } from './EmptyValue';

// ServiceAreaPill - the one way a Service Area appears in a table (W-L5 / W-M7.4).
//
// Tables showed a bare number, "Service Area / 22" (wrapping onto two lines on Assets),
// "Service Area 17" wrapping on Schedule and "Service Area 17 | capacity 5". The rule is now:
// always a single-line pill, "Service Area 16" where the table has room and "SA16" in compressed
// tables (`compact`, chosen per table so every row uses the same form). Never a number alone,
// never wrapped.

export type ServiceAreaLike =
  | number
  | string
  | { id?: number | string | null; name?: string | null; code?: string | null }
  | null
  | undefined;

/** The service area's number, if it has one ("Service Area 16", "SA16", "16", 16, {code:'16'}). */
export function serviceAreaNumber(value: ServiceAreaLike): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'string') {
    const m = value.trim().match(/^(?:service\s*area|sa)?\s*#?\s*0*(\d+)$/i);
    return m ? m[1] : null;
  }
  return (
    serviceAreaNumber(value.code ?? null) ??
    serviceAreaNumber(value.name ?? null) ??
    (value.id !== null && value.id !== undefined && !value.name ? serviceAreaNumber(String(value.id)) : null)
  );
}

/**
 * Display text for a service area outside a pill (headings, detail views, option labels):
 * "Service Area 16" or, compact, "SA16". A non-numeric name is shown as it is.
 */
export function formatServiceArea(value: ServiceAreaLike, compact = false): string | null {
  const n = serviceAreaNumber(value);
  if (n) return compact ? `SA${n}` : `Service Area ${n}`;
  if (typeof value === 'string') return value.trim() || null;
  if (value && typeof value === 'object') return value.name?.trim() || null;
  return null;
}

export interface ServiceAreaPillProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** A number, a code, a name ("Service Area 16") or a `{ id, name, code }` record. */
  value: ServiceAreaLike;
  /** "SA16" instead of "Service Area 16" - for compressed tables. */
  compact?: boolean;
  /** Rendered when there is no service area. Defaults to the shared empty marker. */
  fallback?: React.ReactNode;
}

export const ServiceAreaPill: React.FC<ServiceAreaPillProps> = ({
  value,
  compact = false,
  fallback,
  className = '',
  ...rest
}) => {
  const text = formatServiceArea(value, compact);
  if (!text) return <>{fallback ?? <EmptyValue />}</>;
  // The compact form keeps the full name as a title, so "SA16" is never the only spelling.
  const full = formatServiceArea(value, false) ?? text;
  return (
    <span
      data-slot="service-area"
      // STAT-07 / V2-031: one neutral slate style, so it never competes with status colours.
      className={`${PILL_SHELL} ${STATUS_TONES.secondary} ${className}`}
      title={rest.title ?? full}
      {...rest}
    >
      <span className="tw-min-w-0 tw-overflow-hidden tw-text-ellipsis">{text}</span>
    </span>
  );
};

export default ServiceAreaPill;
